import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const topModal = () => {
  const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return dialogs[dialogs.length - 1] || null;
};

const backgroundSiblings = (root) => {
  const siblings = [];
  let branch = root;

  while (branch?.parentElement && branch.parentElement !== document.body) {
    for (const child of branch.parentElement.children) {
      if (child !== branch) siblings.push(child);
    }
    branch = branch.parentElement;
  }

  return [...new Set(siblings)];
};

/** Focus containment, nested Escape ordering, restoration, and inert background. */
const useModalFocus = (rootRef, onEscape, enabled = true) => {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!enabled) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const previousFocus = document.activeElement;
    const inerted = backgroundSiblings(root).map((element) => ({
      element,
      hadInert: element.hasAttribute('inert'),
    }));

    inerted.forEach(({ element }) => element.setAttribute('inert', ''));
    if (topModal() === root) root.focus();

    const onKeyDown = (event) => {
      if (event.__neoModalHandled || topModal() !== root) return;

      if (event.key === 'Escape') {
        event.__neoModalHandled = true;
        event.preventDefault();
        onEscapeRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...root.querySelectorAll(FOCUSABLE_SELECTOR)];
      if (!focusable.length) {
        event.preventDefault();
        root.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      inerted.forEach(({ element, hadInert }) => {
        if (!hadInert) element.removeAttribute('inert');
      });
      if (previousFocus instanceof HTMLElement && document.contains(previousFocus)) {
        previousFocus.focus();
      }
    };
  }, [rootRef, enabled]);
};

export default useModalFocus;
